import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
    const navigate = useNavigate();
    const { exchangeSession } = useAuth();
    const hasProcessed = useRef(false);
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(true);

    useEffect(() => {
        // Use useRef to prevent double processing in StrictMode
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const processAuth = async () => {
            // Extract session_id from URL fragment
            const hash = window.location.hash;
            const params = new URLSearchParams(hash.substring(1));
            const sessionId = params.get('session_id');

            if (!sessionId) {
                console.error('No session_id found in URL');
                setError('No session ID found. Please try signing in again.');
                setIsProcessing(false);
                return;
            }

            try {
                const userData = await exchangeSession(sessionId);
                
                // Clear the hash from URL
                window.history.replaceState(null, '', window.location.pathname);
                
                // Get pending role from localStorage (set during signup)
                const pendingRole = localStorage.getItem('pending_role');
                localStorage.removeItem('pending_role');
                
                // Navigate based on user role
                if (!userData.role && !pendingRole) {
                    navigate('/role-selection', { replace: true, state: { user: userData } });
                } else if (userData.role === 'STUDENT' || pendingRole === 'STUDENT') {
                    navigate('/student/dashboard', { replace: true, state: { user: userData } });
                } else if (userData.role === 'EMPLOYER' || pendingRole === 'EMPLOYER') {
                    navigate('/employer/dashboard', { replace: true, state: { user: userData } });
                } else {
                    navigate('/role-selection', { replace: true, state: { user: userData } });
                }
            } catch (err) {
                console.error('Auth callback error:', err);
                setError(err.message || 'Authentication failed. Please try again.');
                setIsProcessing(false);
            }
        };

        processAuth();
    }, [exchangeSession, navigate]);

    if (error) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <div className="flex flex-col items-center gap-4 max-w-md text-center">
                    <AlertCircle className="h-12 w-12 text-red-500" />
                    <h2 className="text-xl font-semibold text-foreground">Sign In Failed</h2>
                    <p className="text-muted-foreground">{error}</p>
                    <div className="flex gap-3 mt-4">
                        <Button 
                            variant="outline" 
                            onClick={() => navigate('/login', { replace: true })}
                        >
                            Back to Login
                        </Button>
                        <Button 
                            onClick={() => window.location.reload()}
                        >
                            Try Again
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-foreground font-medium">Completing sign in...</p>
                <p className="text-muted-foreground text-sm">Please wait while we verify your account</p>
            </div>
        </div>
    );
};

export default AuthCallback;
